import { z } from "zod";
import {
  salesEndOutlivesSlot,
  salesEndPrecedesStart,
  salesWindowMessage,
} from "@/domain/offer/rules";
import { endsWhenItStarts, seriesMessage } from "@/domain/offer/series";
import { customFieldRowsSchema } from "@/features/custom-fields/form-schema";
import { slotEnd } from "@/features/offers/slot-end";
import { formatDate } from "@/lib/time";

function slotEndInstant({
  date,
  start_time,
  end_time,
}: {
  date: Date;
  start_time: string;
  end_time: string;
}): string | null {
  const day = formatDate(date, "yyyy-MM-dd");
  if (!day || !start_time || !end_time) return null;
  const end = slotEnd(day, start_time, end_time);
  return Number.isNaN(end.getTime()) ? null : end.toISOString();
}

/** Form-level Zod for OfferFormDialog. Fields are kept as strings for the
 *  date/time pickers — values are coerced + validated at submit time. */
export const offerFormSchema = z
  .object({
    activity_id: z.string().min(1, "Vyberte šablonu slev"),
    date: z.date({ required_error: "Vyberte datum" }),
    start_time: z.string().min(1, "Zadejte čas začátku"),
    end_time: z.string().min(1, "Zadejte čas konce"),
    price_discounted: z.string().min(1, "Zadejte zlevněnou cenu"),
    max_slots: z.string().min(1, "Zadejte počet míst"),
    sale_start: z.string().optional(),
    sale_end: z.string().optional(),
    custom_fields_mode: z.enum(["inherit", "override", "unreadable"]),
    custom_fields: customFieldRowsSchema,
  })
  .superRefine((data, ctx) => {
    if (salesEndPrecedesStart(data.sale_start, data.sale_end)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: salesWindowMessage.endPrecedesStart,
        path: ["sale_end"],
      });
      return;
    }
    if (salesEndOutlivesSlot(data.sale_end, slotEndInstant(data))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: salesWindowMessage.endOutlivesSlot,
        path: ["sale_end"],
      });
    }
  });

export type OfferFormValues = z.infer<typeof offerFormSchema>;

/** Form-level Zod for the generator: the pattern, not the termíny. Price and
 *  capacity are absent on purpose — the šablona carries both. */
export const offerSeriesFormSchema = z
  .object({
    activity_id: z.string().min(1, "Vyberte šablonu slev"),
    weekdays: z.array(z.number()).min(1, seriesMessage.noWeekday),
    start_time: z.string().min(1, "Zadejte čas začátku"),
    end_time: z.string().min(1, "Zadejte čas konce"),
    range_from: z.date({ required_error: "Vyberte začátek období" }),
    range_to: z.date({ required_error: "Vyberte konec období" }),
  })
  .superRefine((data, ctx) => {
    if (endsWhenItStarts(data.start_time, data.end_time)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: seriesMessage.endEqualsStart,
        path: ["end_time"],
      });
    }
  });

export type OfferSeriesFormValues = z.infer<typeof offerSeriesFormSchema>;
